import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverYearRound() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "year-round-fun-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    winter: "Winter in Vancouver",
    spring: "Spring in Vancouver",
    summer: "Summer in Vancouver",
    fall: "Fall in Vancouver",
    highlights: "Must-See Highlights",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Site",
    },
    {
      url: "https://www.hellobc.com/",
      label: "HelloBC – Tourism British Columbia",
    },
    {
      url: "https://vancouver.ca/",
      label: "City of Vancouver – Official Site",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "stanley-park",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park",
    },
    {
      id: "capilano-bridge",
      lat: 49.34278,
      lng: -123.11222,
      popupText: "Capilano Suspension Bridge Park",
    },
    {
      id: "granville-island",
      lat: 49.27083,
      lng: -123.13417,
      popupText: "Granville Island",
    },
    {
      id: "grouse-mountain",
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain",
    },
    {
      id: "vancouver-aquarium",
      lat: 49.30059,
      lng: -123.13105,
      popupText: "Vancouver Aquarium (Stanley Park)",
    },
    {
      id: "gastown-steam-clock",
      lat: 49.28469,
      lng: -123.11095,
      popupText: "Gastown & Steam Clock",
    },
    {
      id: "english-bay-beach",
      lat: 49.28627,
      lng: -123.14359,
      popupText: "English Bay Beach",
    },
    {
      id: "canada-place",
      lat: 49.28864,
      lng: -123.11112,
      popupText: "Canada Place & Waterfront",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Year-Round Fun in Vancouver: Must-See Attractions & Seasonal Activities</title>
        <meta
          name="description"
          content="Discover the best of Vancouver year-round with seasonal activities, top attractions, local regulations, FAQ, and more in this comprehensive guide."
        />
        <meta
          name="keywords"
          content="Vancouver, year-round activities, winter, summer, spring, fall, travel, guide"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/year-round-fun-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Year-Round Fun in Vancouver: Must-See Attractions & Seasonal Activities"
        />
        <meta
          property="og:description"
          content="Discover the best of Vancouver year-round with seasonal activities, top attractions, and local tips in this comprehensive guide."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/year-round-fun-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/year-round-fun-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Year-Round Fun in Vancouver: Must-See Attractions & Seasonal Activities"
        />
        <meta
          name="twitter:description"
          content="Discover the best of Vancouver year-round with seasonal activities, top attractions, and local tips in this comprehensive guide."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/year-round-fun-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Vancouver Skyline with Mountains in Background"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver, nestled between the Pacific Ocean and the Coast
              Mountains, offers a unique blend of urban sophistication and
              breathtaking natural scenery. Thanks to its mild climate, you can
              explore beaches, forests, and city streets year-round. Whether
              you’re a winter sports enthusiast, a cherry blossom chaser, or a
              festival fanatic, Vancouver has something for everyone. This
              guide showcases seasonal highlights, must-see attractions, and
              practical advice to help you enjoy all the city has to offer—
              anytime of the year.
            </p>

            {/* SECTION: Winter */}
            <h2 id="winter" ref={(el) => setSpotRef("winter", el)}>
              Winter in Vancouver (December – February)
            </h2>
            <p>
              Despite its northern location, Vancouver’s winters are generally
              mild and wet, with rain in the city and snow on the local
              mountains. Pack a waterproof jacket and layer up to stay cozy. On
              clear days, you’ll be treated to beautiful views of the snow-capped
              North Shore mountains.
            </p>
            <ul>
              <li>
                <strong>Mountains & Snow Sports:</strong> Grouse Mountain, Cypress
                Mountain, and Mount Seymour offer skiing, snowboarding, and
                snowshoeing. For an iconic winter experience, head up to&nbsp;
                <strong>Grouse Mountain</strong>{" "}
                <Marker
                  coordY={49.37956}
                  coordX={-123.08180}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                to enjoy the Skyride tram, light displays, and even a visit to
                its resident grizzly bears in their habitat (when they’re not
                hibernating).
              </li>
              <li>
                <strong>Festive Lights:</strong> During the holiday season,
                Canyon Lights at&nbsp;
                <strong>Capilano Suspension Bridge Park</strong>{" "}
                <Marker
                  coordY={49.34278}
                  coordX={-123.11222}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                and the VanDusen Festival of Lights transform forest paths and
                gardens into a sparkling winter wonderland.
              </li>
              <li>
                <strong>Indoor Attractions:</strong> If it’s too rainy, check out
                museums like the Vancouver Art Gallery or explore the{" "}
                <strong>Vancouver Aquarium</strong>{" "}
                <Marker
                  coordY={49.30059}
                  coordX={-123.13105}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                in Stanley Park.
              </li>
            </ul>

            {/* SECTION: Spring */}
            <h2 id="spring" ref={(el) => setSpotRef("spring", el)}>
              Spring in Vancouver (March – May)
            </h2>
            <p>
              Spring brings the iconic cherry blossoms, typically from late March
              through April, making Vancouver streets a pastel dream. Temperatures
              rise gradually, and you’ll see nature in full bloom.
            </p>
            <ul>
              <li>
                <strong>Cherry Blossom Festival:</strong> The Vancouver Cherry
                Blossom Festival celebrates the city’s 40,000+ cherry trees with
                guided walks and cultural events. Neighborhoods like Kitsilano
                and the West End are lined with blossoms.
              </li>
              <li>
                <strong>Parks & Gardens:</strong> Stroll through
                <strong>Stanley Park</strong>{" "}
                <Marker
                  coordY={49.30000}
                  coordX={-123.14000}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                for fresh spring air, or visit VanDusen Botanical Garden and Queen
                Elizabeth Park for vibrant flowers and greenery.
              </li>
              <li>
                <strong>Whale Watching & Mild Hikes:</strong> Spring is also the
                start of whale watching season. Meanwhile, trails in Lynn Canyon
                or Dog Mountain begin to thaw, making them accessible to hikers
                looking for moderate adventures.
              </li>
            </ul>

            {/* SECTION: Summer */}
            <h2 id="summer" ref={(el) => setSpotRef("summer", el)}>
              Summer in Vancouver (June – August)
            </h2>
            <p>
              Long days and plenty of sunshine make summer the peak tourist
              season. Expect temperatures around the low to mid-20s °C (70s °F)
              and very little rain.
            </p>
            <ul>
              <li>
                <strong>Beaches & Water Activities:</strong> English Bay Beach{" "}
                <Marker
                  coordY={49.28627}
                  coordX={-123.14359}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                is a local favorite for swimming and sunsets. You can also rent a
                kayak or paddleboard around False Creek or English Bay.
              </li>
              <li>
                <strong>Festivals & Events:</strong> Catch the Celebration of
                Light fireworks competition at English Bay or enjoy open-air
                Shakespeare performances at Bard on the Beach in Kitsilano.
              </li>
              <li>
                <strong>Outdoor Markets & Dining:</strong> Explore{" "}
                <strong>Granville Island</strong>{" "}
                <Marker
                  coordY={49.27083}
                  coordX={-123.13417}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                Public Market for fresh produce and local artisans, or dine on
                patios across the city with scenic views.
              </li>
            </ul>

            {/* SECTION: Fall */}
            <h2 id="fall" ref={(el) => setSpotRef("fall", el)}>
              Fall in Vancouver (September – November)
            </h2>
            <p>
              Early fall often feels like an extension of summer, with comfortably
              warm days and fewer crowds. By late October, crisp air and vibrant
              foliage take over, and rain becomes more frequent.
            </p>
            <ul>
              <li>
                <strong>Fall Foliage:</strong> Maples and other deciduous trees
                turn brilliant red and gold in{" "}
                <strong>Stanley Park</strong>{" "}
                <Marker
                  coordY={49.30000}
                  coordX={-123.14000}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                and Queen Elizabeth Park.
              </li>
              <li>
                <strong>Harvest Events:</strong> Enjoy farmers’ markets, pumpkin
                patches, and seasonal treats around the city (like fresh apple
                cider).
              </li>
              <li>
                <strong>Shoulder Season Perks:</strong> It’s a great time for
                deals on accommodation and smaller crowds at major attractions
                before winter sets in.
              </li>
            </ul>

            {/* SECTION: Must-See Highlights */}
            <h2 id="highlights" ref={(el) => setSpotRef("highlights", el)}>
              Must-See Highlights
            </h2>
            <p>
              Vancouver brims with year-round attractions that adapt to the
              seasons:
            </p>
            <ol>
              <li>
                <strong>Stanley Park</strong>{" "}
                <Marker
                  coordY={49.30000}
                  coordX={-123.14000}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Canada’s top urban park, perfect for walking the Seawall,
                picnics, and soaking in ocean views. Home to the{" "}
                <strong>Vancouver Aquarium</strong>{" "}
                <Marker
                  coordY={49.30059}
                  coordX={-123.13105}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                .
              </li>
              <li>
                <strong>Capilano Suspension Bridge Park</strong>{" "}
                <Marker
                  coordY={49.34278}
                  coordX={-123.11222}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Famous 140m-long bridge, cliff walks, and treetop adventures in
                a coastal rainforest setting.
              </li>
              <li>
                <strong>Granville Island</strong>{" "}
                <Marker
                  coordY={49.27083}
                  coordX={-123.13417}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Public Market, artisan shops, restaurants, live theatre, and
                scenic marinas.
              </li>
              <li>
                <strong>Gastown & Steam Clock</strong>{" "}
                <Marker
                  coordY={49.28469}
                  coordX={-123.11095}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Vancouver’s oldest neighborhood with cobblestone streets, the
                iconic steam clock, and trendy dining.
              </li>
              <li>
                <strong>English Bay Beach</strong>{" "}
                <Marker
                  coordY={49.28627}
                  coordX={-123.14359}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Enjoy sunsets, swimming, and people-watching along the downtown
                shoreline.
              </li>
              <li>
                <strong>Canada Place & Waterfront</strong>{" "}
                <Marker
                  coordY={49.28864}
                  coordX={-123.11112}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Iconic sail-like architecture, harbor views, and home to the
                cruise terminal. Check out FlyOver Canada.
              </li>
            </ol>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations" ref={(el) => setSpotRef("local-regulations", el)}>
              Local Regulations & Best Practices
            </h2>
            <p>
              <strong>Smoking & Alcohol:</strong> Smoking or vaping is prohibited
              in Vancouver’s parks and beaches by city bylaw. Alcohol is only
              allowed in licensed establishments or certain pilot project
              parks—always check posted signs. Cannabis is legal for adults 19+,
              but the same public smoking rules apply.
            </p>
            <p>
              <strong>Cycling:</strong> Helmets are mandatory for cyclists of all
              ages in British Columbia. Stay in bike lanes where possible and
              follow traffic signals.
            </p>
            <p>
              <strong>Wildlife & Environment:</strong> Never feed wildlife (including
              birds), and keep a respectful distance from raccoons, coyotes, or
              bears. Vancouver is proud of its green spaces—stick to marked
              trails, and dispose of litter in provided bins.
            </p>
            <p>
              <strong>Driving:</strong> Handheld phone use while driving is
              illegal. Speed limits: 50 km/h in the city unless otherwise posted,
              30 km/h in school/residential zones. Watch for pedestrian
              crosswalks and cyclists.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. What is the best season to visit Vancouver?</h3>
              <p>
                Vancouver truly shines all year. Summer is dry and vibrant with
                festivals, while winter offers snow sports on the mountains.
                Spring brings cherry blossoms, and fall has stunning foliage.
                Choose your season based on whether you prefer beaches, skiing,
                or mild hikes among blooms.
              </p>

              <h3>2. Can I explore without a car?</h3>
              <p>
                Yes. Vancouver’s public transit (SkyTrain, buses, SeaBus) covers
                most attractions. The city is bike-friendly and walkable,
                especially downtown. A car is handy if you plan side trips to
                Whistler or other remote areas, but it’s not essential within the
                city.
              </p>

              <h3>3. How many days do I need to see the highlights?</h3>
              <p>
                Plan at least 3–4 days for a comfortable pace covering Stanley
                Park, downtown, Capilano Suspension Bridge, and maybe a mountain
                visit (Grouse). With a week, you can add day trips to Whistler or
                Victoria, or do more in-depth exploring in different
                neighborhoods.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              From festive winter lights to summer sunsets, spring blossoms to
              autumn leaves, Vancouver is captivating in every season. This
              all-season city invites you to explore Stanley Park’s natural
              beauty, taste the fresh offerings of Granville Island’s market, or
              soak in the vibrant neighborhoods like Gastown. Plan around your
              favorite activities—whether it’s skiing, sightseeing, or simply
              indulging in local cuisine—and you’ll find Vancouver ready to
              delight you all year long.
            </p>
            <p>
              <em>
                Disclaimer: Always check local regulations and weather forecasts
                before planning your activities. Conditions can change quickly,
                especially in the mountains. Follow official guidelines for park
                usage and safety.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}